import { useCallback, useMemo, useState } from 'react';
import {
  alignWithReaction,
  arrowOf,
  boxScale,
  componentsOf,
  reactionParts,
  reactionTexts,
  settledByArrow
} from '@peruselab/structure-editor/domain/reactionBoxes';
import { alignObjects, type ReactionParts } from '@peruselab/structure-editor/domain/objectAlignment';
import { readReactionText, textOf, withFormulaSubscripts } from '@peruselab/structure-editor/chemistry/reactionText';
import type { DrawingDocument } from '@peruselab/structure-editor/model';
import type { DocumentHistory } from '@peruselab/structure-editor/domain/history';
import { moveAtoms } from '@peruselab/structure-editor/domain/atomGeometryMutations';
import { saltsAsOne } from '@peruselab/structure-editor/domain/ionicPairs';
import {
  boxMembershipAsDrawn,
  defaultBoxes,
  growBox,
  translateRect,
  type BoxedMolecule,
  type ReactionArrowGeometry,
  type ReactionBoxes,
  type ReactionZone,
  type ReactionZoneBox
} from '@peruselab/structure-editor/domain/reactionGraph';

type BoxCache = WeakMap<DrawingDocument, ReactionBoxes | null>;

interface Shown {
  readonly document: DrawingDocument;
  readonly boxes: ReactionBoxes | null;
}

export interface ReactionBoxesState {
  /** The drawing's reaction arrow (document units), or null when it has none. */
  readonly arrow: ReactionArrowGeometry | null;
  /** Each molecule (document units) with the box it is in -- null zone = loose. */
  readonly molecules: readonly BoxedMolecule[];
  /** The boxes as drawn (document units): grown to fit, never below their own size. */
  readonly boxes: Readonly<Record<ReactionZone, ReactionZoneBox>> | null;
  /** #1151 -- the reaction's parts as Align and Distribute see them; null without an arrow. */
  readonly parts: ReactionParts | null;
}

/** Boxes for the history's present entry: cached per entry (so undo/redo land on that entry's
 *  boxes), carried over from the last shown entry for an uncached one (a tool commit, or a
 *  write-back without history), created around a newly placed arrow, and gone without an arrow. */
function boxesForPresent(document: DrawingDocument, cache: BoxCache, shown: Shown | null): ReactionBoxes | null {
  if (cache.has(document)) return cache.get(document)!;
  const arrow = arrowOf(document);
  const boxes = !arrow ? null : (shown?.boxes ?? defaultBoxes(componentsOf(document), arrow, boxScale(document)));
  cache.set(document, boxes);
  return boxes;
}

/**
 * ADR 0014 -- the reaction boxes (owner, 2026-09-26): once the drawing has a reaction arrow, four
 * boxes surround it, each with its own position and size, layered over the document history the
 * same way the Markush layers are (useMarkushMode) -- cached per entry, never in the document.
 */
export function useReactionBoxes(
  history: DocumentHistory,
  commitDocument: (change: (current: DrawingDocument) => DrawingDocument) => void
) {
  const document = history.present;
  const [cache, setCache] = useState<BoxCache>(() => new WeakMap());
  const [shown, setShown] = useState<Shown | null>(null);

  const stored = useMemo(() => boxesForPresent(document, cache, shown), [cache, document, shown]);
  if (shown?.document !== document) setShown({ document, boxes: stored });

  /** Forget every entry's boxes: a different drawing is about to be loaded. */
  const resetReactionBoxes = useCallback(() => {
    setCache(new WeakMap());
    setShown(null);
  }, []);

  const state = useMemo<ReactionBoxesState>(() => {
    const arrow = arrowOf(document);
    if (!arrow || !stored) return { arrow: null, molecules: [], boxes: null, parts: null };
    // #1134: in a box is inside it as drawn -- what the reader sees is what Save keeps.
    const { molecules, shown } = boxMembershipAsDrawn(componentsOf(document), stored, boxScale(document).padding);
    // #1134: a salt's ions in one box are one structure there -- no "+" between them, one participant.
    return { arrow, molecules: saltsAsOne(document, molecules), boxes: shown, parts: reactionParts(document, stored) };
  }, [document, stored]);

  /** Moves a box and every molecule in it by (dx, dy) document units -- one undo step. */
  const moveBox = useCallback(
    (zone: ReactionZone, dx: number, dy: number) => {
      if (!stored || (dx === 0 && dy === 0)) return;
      const { width, height, coordinates } = document.depiction.coordinateSet;
      const memberIds = state.molecules
        .filter((molecule) => molecule.zone === zone)
        .flatMap((molecule) => molecule.atomIds);
      const moved = moveAtoms(
        document,
        Object.fromEntries(
          memberIds.map((id) => [id, { x: (coordinates[id]!.x + dx) / width, y: (coordinates[id]!.y + dy) / height }])
        )
      );
      // Always a fresh entry (an empty box moves only itself; moveAtoms returns the same object when
      // nothing moved), so this move is its own undo step with its own boxes.
      const next = moved === document ? { ...document } : moved;
      cache.set(next, { ...stored, [zone]: translateRect(stored[zone], dx, dy) });
      commitDocument(() => next);
    },
    [cache, commitDocument, document, state.molecules, stored]
  );

  /** #698 -- reverses the reaction arrow: its ends swap, and so do the Reactants and Products boxes
   *  (each keeping its contents), so what was behind the arrow is now past it. One undo step. */
  const reverseArrow = useCallback(
    (arrowId: string) => {
      const next = {
        ...document,
        annotations: document.annotations.map((annotation) =>
          annotation.id === arrowId && annotation.kind === 'reaction-arrow'
            ? { ...annotation, from: annotation.to, to: annotation.from }
            : annotation
        )
      };
      if (stored) cache.set(next, { ...stored, reactants: stored.products, products: stored.reactants });
      commitDocument(() => next);
    },
    [cache, commitDocument, document, stored]
  );

  /** #700 -- commits `next` (the drawing with `atomIds` just added) with the `zone` box grown to
   *  hold them -- one undo step -- so what was dropped into / added to a box is in it. */
  const commitIntoBox = useCallback(
    (zone: ReactionZone, next: DrawingDocument, atomIds: readonly string[]) => {
      if (!stored) {
        commitDocument(() => next);
        return;
      }
      const { coordinates } = next.depiction.coordinateSet;
      const points = atomIds.flatMap((id) => (coordinates[id] ? [coordinates[id]] : []));
      if (points.length > 0) {
        const bounds = {
          minX: Math.min(...points.map((point) => point.x)),
          minY: Math.min(...points.map((point) => point.y)),
          maxX: Math.max(...points.map((point) => point.x)),
          maxY: Math.max(...points.map((point) => point.y))
        };
        cache.set(next, { ...stored, [zone]: growBox(stored[zone], bounds, boxScale(next).padding) });
      }
      commitDocument(() => next);
    },
    [cache, commitDocument, stored]
  );

  /** #744 -- commits `next`, a reaction just placed on the paper, with its boxes fitted around
   *  its own molecules (`atomIds`) only -- molecules already loose on the paper stay loose. */
  const commitReaction = useCallback(
    (next: DrawingDocument, atomIds: readonly string[]) => {
      const arrow = arrowOf(next);
      if (arrow) {
        const own = new Set(atomIds);
        const components = componentsOf(next).filter((component) => component.atomIds.some((id) => own.has(id)));
        cache.set(next, defaultBoxes(components, arrow, boxScale(next)));
      }
      commitDocument(() => next);
    },
    [cache, commitDocument]
  );

  /** #1137 follow-up (owner) -- what was just dropped or moved (`atomIds`, `annotationIds`) into the
   *  Reagents & catalysts or Solvents box settles by the arrow: its own undo step, so Undo puts it
   *  back where it was dropped. Nothing settles outside those boxes. */
  const settleByArrow = useCallback(
    (atomIds: Iterable<string>, annotationIds: Iterable<string>) => {
      const atoms = [...atomIds];
      const texts = [...annotationIds];
      if (!stored || (atoms.length === 0 && texts.length === 0)) return;
      commitDocument((current) => {
        const boxes = cache.get(current) ?? stored;
        const next = settledByArrow(current, atoms, texts, boxes);
        if (next !== current) cache.set(next, boxes);
        return next;
      });
    },
    [cache, commitDocument, stored]
  );

  /** #1151 (owner) -- Align/Distribute with the reaction arranged as such, its boxes moving with
   *  what they hold, in the same undo step. Plain Align/Distribute without a reaction. */
  const alignInReaction = useCallback<typeof alignObjects>(
    (current, selection, kind, options = {}) => {
      const boxes = cache.get(current) ?? stored;
      if (!boxes) return alignObjects(current, selection, kind, options);
      const aligned = alignWithReaction(current, selection, kind, options, boxes);
      if (aligned.document !== current) cache.set(aligned.document, aligned.boxes);
      return aligned.document;
    },
    [cache, stored]
  );

  /** #1137 follow-up (owner) -- a text by the arrow just finished: the counts in the formulas it
   *  names set as subscripts (H2SO4 reads H₂SO₄), and the text settled by the arrow -- one undo step. */
  const finishReactionText = useCallback(
    (id: string) => {
      if (!stored) return;
      commitDocument((current) => {
        const boxes = cache.get(current) ?? stored;
        if (!boxes) return current;
        const shown = boxMembershipAsDrawn(componentsOf(current), boxes, boxScale(current).padding).shown;
        const text = reactionTexts(current, shown).find(({ annotation }) => annotation.id === id)?.annotation;
        if (!text) return current;
        const names = readReactionText(textOf(text)).reagents.map((reagent) => reagent.name);
        const paragraphs = withFormulaSubscripts(text.paragraphs, names);
        const formatted =
          paragraphs === text.paragraphs
            ? current
            : {
                ...current,
                annotations: current.annotations.map((annotation) =>
                  annotation === text ? { ...text, paragraphs: paragraphs as typeof text.paragraphs } : annotation
                )
              };
        const next = settledByArrow(formatted, [], [id], boxes);
        if (next !== current) cache.set(next, boxes);
        return next;
      });
    },
    [cache, commitDocument, stored]
  );

  return {
    alignInReaction,
    settleByArrow,
    finishReactionText,
    reactionBoxes: state,
    resetReactionBoxes,
    commitReaction,
    moveReactionBox: moveBox,
    reverseReactionArrow: reverseArrow,
    commitIntoBox
  };
}
